/**
 * The canonical modal enter/exit recipe — the single source of truth for
 * modal motion (VISUAL_STYLE_GUIDE.md § Motion): 200ms in / 200ms out,
 * fade on the overlay, zoom + slide on the panel, all gated by
 * `motion-safe:` (the sanctioned reduced-motion mechanism — never a JS
 * `matchMedia` read, never a bare ungated animation).
 *
 * Two spellings of the same recipe:
 *
 * - `MODAL_*_ENTER` / `MODAL_*_EXIT` — for render-prop consumers that
 *   branch on react-aria's `state.isEntering` / `state.isExiting`
 *   (see `modal.tsx`, `fullscreen-modal.tsx`).
 * - `MODAL_*_ANIM` — for consumers that pass a static `className` to a
 *   react-aria `ModalOverlay` / `Modal`; these key off the
 *   `data-entering` / `data-exiting` attributes react-aria stamps.
 *
 * Do NOT respell these classes at a call site — new modal surfaces import
 * from here (or, better, use the `lib/base` Modal wrapper outright).
 */

export const MODAL_OVERLAY_ENTER =
  'motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200 motion-safe:ease-out';

export const MODAL_OVERLAY_EXIT =
  'motion-safe:animate-out motion-safe:fade-out motion-safe:duration-200 motion-safe:ease-in';

export const MODAL_PANEL_ENTER =
  'motion-safe:animate-in motion-safe:zoom-in-95 motion-safe:slide-in-from-bottom-4 motion-safe:duration-200 motion-safe:ease-out';

export const MODAL_PANEL_EXIT =
  'motion-safe:animate-out motion-safe:zoom-out-95 motion-safe:duration-200 motion-safe:ease-in';

/** Static-className spelling of the overlay recipe (both phases). */
export const MODAL_OVERLAY_ANIM =
  'data-[entering]:motion-safe:animate-in data-[entering]:motion-safe:fade-in data-[entering]:motion-safe:duration-200 data-[entering]:motion-safe:ease-out ' +
  'data-[exiting]:motion-safe:animate-out data-[exiting]:motion-safe:fade-out data-[exiting]:motion-safe:duration-200 data-[exiting]:motion-safe:ease-in';

/** Static-className spelling of the panel recipe (both phases). */
export const MODAL_PANEL_ANIM =
  'data-[entering]:motion-safe:animate-in data-[entering]:motion-safe:zoom-in-95 data-[entering]:motion-safe:slide-in-from-bottom-4 data-[entering]:motion-safe:duration-200 data-[entering]:motion-safe:ease-out ' +
  'data-[exiting]:motion-safe:animate-out data-[exiting]:motion-safe:zoom-out-95 data-[exiting]:motion-safe:duration-200 data-[exiting]:motion-safe:ease-in';
